import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./Footer.module.css";
import {
  APP_DATA_FETCHED_EVENT,
  getLastAppDataFetchedAt,
} from "../../Hooks/useAppResumeRefresh";
import { gatherImageAttributions } from "../../Hooks/gatherImageAttributions";

/** Same id stamped into the bundle at build time (see scripts/build-non-split.js). */
const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";
const COPYRIGHT_YEAR = new Date().getFullYear();

function formatFetchedAt(ts) {
  if (ts == null) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return new Date(ts).toString();
  }
}

const Footer = () => {
  const { content } = useSelector((state) => state.contentData);
  const imageCredits = useMemo(
    () => gatherImageAttributions(content),
    [content],
  );

  const [lastDataFetchAt, setLastDataFetchAt] = useState(() =>
    getLastAppDataFetchedAt(),
  );

  useEffect(() => {
    const sync = (event) => {
      const fromEvent = event && event.detail && event.detail.at;
      setLastDataFetchAt(
        typeof fromEvent === "number" ? fromEvent : getLastAppDataFetchedAt(),
      );
    };

    window.addEventListener(APP_DATA_FETCHED_EVENT, sync);
    // In case mark ran just before Footer mounted on first paint.
    sync();

    return () => {
      window.removeEventListener(APP_DATA_FETCHED_EVENT, sync);
    };
  }, []);

  const lastDataLabel = formatFetchedAt(lastDataFetchAt);

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <ul className={styles.rows}>
          <li className={`${styles.row} ${styles["row-side"]}`}>
            <span className={styles.label}>Developer &amp; Designer</span>
            <a
              className={styles.link}
              href="https://www.glassinteractive.com/about-me/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mike Glass
            </a>
          </li>
          <li className={`${styles.row} ${styles["row-contact"]}`}>
            <span className={styles.label}>Contact</span>
            <a
              className={`${styles.link} ${styles["link-email"]}`}
              href="mailto:connect@ignite-elysium.com"
            >
              connect@ignite-elysium.com
            </a>
          </li>
          <li className={`${styles.row} ${styles["row-side"]}`}>
            <span className={styles.label}>More apps &amp; info</span>
            <a
              className={styles.link}
              href="https://www.glassinteractive.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              glassinteractive.com
            </a>
          </li>
        </ul>

        <p className={styles.copyright}>© {COPYRIGHT_YEAR} Ignite Elysium</p>

        {imageCredits.length > 0 && (
          <section className={styles.credits} aria-label="Image credits">
            <h2 className={styles["credits-heading"]}>Image credits</h2>
            <ul className={styles["credits-list"]}>
              {imageCredits.map((credit) => (
                <li
                  key={`${credit.label}|${credit.url || ""}`}
                  className={styles["credits-item"]}
                >
                  {credit.url ? (
                    <a
                      className={styles["credits-link"]}
                      href={credit.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {credit.label}
                    </a>
                  ) : (
                    <span className={styles["credits-text"]}>
                      {credit.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className={styles.meta}>
          <p
            className={styles["meta-line"]}
            title={
              lastDataFetchAt != null
                ? `Last data refresh: ${new Date(lastDataFetchAt).toISOString()}`
                : "Last data refresh: unknown"
            }
          >
            <span className={styles["meta-label"]}>Data refreshed</span>
            <span className={styles["meta-value"]}>{lastDataLabel}</span>
          </p>
          <p className={styles["meta-line"]} title={`Build ${BUILD_ID}`}>
            <span className={styles["meta-label"]}>Build</span>
            <span className={styles["meta-value"]}>{BUILD_ID}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
